/**
 * PRODUCT PAGE - Hidden Variant Automatic Switcher
 * Automatically switches variants based on quantity for tiered discounts
 * Compatible with Dawn, Spotlight, Refresh, Impulse and other Shopify themes
 */

(function() {
  'use strict';

  // ============================================
  // CONFIGURATION
  // ============================================
  
  /**
   * Define your variant rules here
   * Format: { productId: { minQty: variantId } }
   * 
   * Example:
   * const VARIANT_RULES = {
   *   '7234567890': {  // Product ID
   *     1: '41234567890',    // Variant A (1-39 units)
   *     40: '41234567891',   // Variant B (40-99 units)
   *     100: '41234567892'   // Variant C (100+ units)
   *   }
   * };
   */
  const VARIANT_RULES = window.variantRules || {};
  const VARIANT_RULES_ADVANCED = window.variantRulesAdvanced || {};

  // ============================================
  // UTILITY FUNCTIONS
  // ============================================

  /**
   * Get currently selected option value (e.g., size)
   */
  function getSelectedOptionValue(form, optionIndex = 0) {
    console.log('[Variant Switcher] Getting selected option, index:', optionIndex);
    
    // Method 1: Try variant-picker custom element with data-selected-variant
    // Look in form first, then in document (variant-picker might be outside form)
    let variantPicker = form.querySelector('variant-picker');
    if (!variantPicker) {
      variantPicker = document.querySelector('variant-picker');
      console.log('[Variant Switcher] Variant picker found outside form');
    }
    
    if (variantPicker) {
      const selectedVariantScript = variantPicker.querySelector('script[data-selected-variant]');
      if (selectedVariantScript) {
        try {
          const selectedVariant = JSON.parse(selectedVariantScript.textContent);
          console.log('[Variant Switcher] Selected variant from JSON:', selectedVariant);
          if (selectedVariant && selectedVariant.options && selectedVariant.options[optionIndex]) {
            const optionValue = selectedVariant.options[optionIndex];
            console.log('[Variant Switcher] Option value from JSON:', optionValue);
            return optionValue;
          }
        } catch (e) {
          console.warn('[Variant Switcher] Error parsing selected variant JSON:', e);
        }
      }
    }
    
    // Method 2: Try to find selected variant options from inputs
    const selectors = [
      `select[name="options[${optionIndex}]"]`,
      `input[name="options[${optionIndex}]"]:checked`,
      `.variant-input-wrapper:nth-child(${optionIndex + 1}) input:checked`,
      `.variant-input-wrapper:nth-child(${optionIndex + 1}) select`,
      `fieldset:nth-child(${optionIndex + 1}) input:checked`,
      `fieldset:nth-child(${optionIndex + 1}) select`
    ];

    for (let selector of selectors) {
      const input = form.querySelector(selector);
      if (input) {
        console.log('[Variant Switcher] Found option from selector:', selector, 'value:', input.value);
        return input.value;
      }
    }

    // Method 3: Try to get from current variant ID
    const variantInput = findVariantInput(form);
    if (variantInput && variantInput.value) {
      const variantId = variantInput.value;
      console.log('[Variant Switcher] Trying to get option from variant ID:', variantId);
      
      // Try to find variant data in window
      if (window.ShopifyAnalytics && window.ShopifyAnalytics.meta && window.ShopifyAnalytics.meta.product) {
        const product = window.ShopifyAnalytics.meta.product;
        if (product.variants) {
          const variant = product.variants.find(v => v.id.toString() === variantId.toString());
          if (variant && variant.options && variant.options[optionIndex]) {
            console.log('[Variant Switcher] Found option from ShopifyAnalytics:', variant.options[optionIndex]);
            return variant.options[optionIndex];
          }
        }
      }
    }

    console.warn('[Variant Switcher] Could not find selected option value');
    return null;
  }

  /**
   * Get the correct variant ID based on quantity (simple mode)
   */
  function getVariantIdForQuantity(productId, quantity) {
    const rules = VARIANT_RULES[productId];
    if (!rules) return null;

    // Sort thresholds in descending order
    const thresholds = Object.keys(rules)
      .map(Number)
      .sort((a, b) => b - a);

    // Find the appropriate variant
    for (let threshold of thresholds) {
      if (quantity >= threshold) {
        return rules[threshold];
      }
    }

    return null;
  }

  /**
   * Get the correct variant ID based on option value and quantity (advanced mode)
   */
  function getVariantIdForQuantityAdvanced(productId, optionValue, quantity) {
    const rules = VARIANT_RULES_ADVANCED[productId];
    if (!rules) return null;

    const optionRules = rules[optionValue];
    if (!optionRules) return null;

    // Sort thresholds in descending order
    const thresholds = Object.keys(optionRules)
      .map(Number)
      .sort((a, b) => b - a);

    // Find the appropriate variant
    for (let threshold of thresholds) {
      if (quantity >= threshold) {
        return optionRules[threshold];
      }
    }

    return null;
  }

  /**
   * Find quantity input - supports multiple theme structures
   */
  function findQuantityInput(form) {
    const selectors = [
      'input[name="quantity"]',
      'input[type="number"][name="quantity"]',
      'quantity-input input',
      '.quantity__input',
      '[data-quantity-variant-id]',
      'input.quantity__input'
    ];

    for (let selector of selectors) {
      const input = form.querySelector(selector);
      if (input) return input;
    }

    return null;
  }

  /**
   * Find variant ID input - supports multiple theme structures
   */
  function findVariantInput(form) {
    const selectors = [
      'input[name="id"]',
      'select[name="id"]',
      '[name="id"]'
    ];

    for (let selector of selectors) {
      const input = form.querySelector(selector);
      if (input) return input;
    }

    return null;
  }

  /**
   * Get product ID from form or page
   */
  function getProductId(form) {
    // Try to get from form data attribute
    const productInfo = form.closest('[data-product-id]');
    if (productInfo) {
      return productInfo.dataset.productId;
    }

    // Try to get from meta tag
    const metaProduct = document.querySelector('meta[property="product:id"]');
    if (metaProduct) {
      return metaProduct.content;
    }

    // Try to get from window object
    if (window.ShopifyAnalytics && window.ShopifyAnalytics.meta && window.ShopifyAnalytics.meta.product) {
      return window.ShopifyAnalytics.meta.product.id.toString();
    }

    return null;
  }

  /**
   * Update variant input value
   */
  function updateVariantInput(variantInput, newVariantId) {
    if (variantInput.tagName === 'SELECT') {
      // For select elements
      const option = variantInput.querySelector(`option[value="${newVariantId}"]`);
      if (option) {
        variantInput.value = newVariantId;
        variantInput.dispatchEvent(new Event('change', { bubbles: true }));
      }
    } else {
      // For input elements
      variantInput.value = newVariantId;
      variantInput.dispatchEvent(new Event('change', { bubbles: true }));
    }
  }

  /**
   * Update price display with strikethrough for discounted prices
   */
  function updatePriceDisplay(form, variantId, productId, selectedOption, quantity) {
    // Thresholds based on requirements:
    // 1-39: Tier 1 (No Discount)
    // 40-99: Tier 2 (Discounted)
    // 100+: Tier 3 (Discounted)
    const DISCOUNT_THRESHOLD = 40;

    // Trigger variant change event for theme to update price
    const changeEvent = new CustomEvent('variant:change', {
      bubbles: true,
      detail: { variant: variantId }
    });
    form.dispatchEvent(changeEvent);

    // Also dispatch standard change event
    form.dispatchEvent(new Event('change', { bubbles: true }));
    
    // Handle strikethrough price based on quantity
    if (quantity < DISCOUNT_THRESHOLD) {
      // Remove strikethrough for Tier 1 (1-39) - NO DISCOUNT
      removeStrikethroughPrice(form);
    } else {
      // Show strikethrough for Tier 2 (40-99) and Tier 3 (100+)
      const basePrices = window.variantBasePrices || {};
      if (basePrices[productId] && basePrices[productId][selectedOption]) {
        const basePrice = basePrices[productId][selectedOption];
        setTimeout(() => {
          addStrikethroughPrice(form, basePrice);
        }, 100);
      }
    }
  }
  
  /**
   * Add strikethrough price display
   */
  function addStrikethroughPrice(form, basePrice) {
    // Find price element
    const priceSelectors = [
      '.price__regular .price-item--regular',
      '.price-item--regular',
      '.product__price',
      '[data-product-price]',
      '.price'
    ];
    
    let priceElement = null;
    for (let selector of priceSelectors) {
      priceElement = form.closest('.product').querySelector(selector) || 
                     document.querySelector(selector);
      if (priceElement) break;
    }
    
    if (!priceElement) return;
    
    // Check if strikethrough already exists
    let comparePrice = priceElement.querySelector('.price-item--compare');
    if (!comparePrice) {
      // Create compare price element
      comparePrice = document.createElement('s');
      comparePrice.className = 'price-item price-item--compare';
      comparePrice.style.marginRight = '8px';
      comparePrice.style.color = '#999';
      
      // Insert before current price
      priceElement.insertBefore(comparePrice, priceElement.firstChild);
    }
    
    // Format price with currency
    const formattedPrice = new Intl.NumberFormat('de-DE', {
      style: 'currency',
      currency: 'EUR'
    }).format(basePrice);
    
    comparePrice.textContent = formattedPrice;
  }
  
  /**
   * Remove strikethrough price display
   */
  function removeStrikethroughPrice(form) {
    const priceSelectors = [
      '.price__regular .price-item--regular',
      '.price-item--regular',
      '.product__price',
      '[data-product-price]',
      '.price'
    ];
    
    for (let selector of priceSelectors) {
      const priceElement = form.closest('.product')?.querySelector(selector) || 
                          document.querySelector(selector);
      if (priceElement) {
        const comparePrice = priceElement.querySelector('.price-item--compare');
        if (comparePrice) {
          comparePrice.remove();
        }
      }
    }
  }

  // ============================================
  // MAIN LOGIC
  // ============================================

  /**
   * Enforce minimum quantity on input
   */
  function enforceMinimumQuantity(quantityInput) {
    const minQuantity = parseInt(quantityInput.getAttribute('min')) || 
                       parseInt(quantityInput.getAttribute('data-min')) || 1;
    const currentValue = parseInt(quantityInput.value) || 1;
    
    if (currentValue < minQuantity) {
      console.log(`[Variant Switcher] Enforcing minimum quantity: ${currentValue} -> ${minQuantity}`);
      quantityInput.value = minQuantity;
      return true;
    }
    return false;
  }

  /**
   * Handle quantity change on product form
   */
  function handleQuantityChange(form, quantityInput) {
    const productId = getProductId(form);
    console.log('[Variant Switcher] Product ID:', productId);
    
    if (!productId) {
      console.warn('[Variant Switcher] No product ID found');
      return;
    }

    const quantity = parseInt(quantityInput.value) || 1;
    console.log('[Variant Switcher] Quantity:', quantity);
    
    let newVariantId = null;
    let selectedOption = null;

    // Check if product uses advanced rules (with size/option)
    if (VARIANT_RULES_ADVANCED[productId]) {
      console.log('[Variant Switcher] Using advanced rules');
      selectedOption = getSelectedOptionValue(form, 0); // Get first option (e.g., size)
      console.log('[Variant Switcher] Selected option:', selectedOption);
      
      if (selectedOption) {
        newVariantId = getVariantIdForQuantityAdvanced(productId, selectedOption, quantity);
        console.log(`[Variant Switcher] Advanced mode - Size: ${selectedOption}, Quantity: ${quantity}, New Variant: ${newVariantId}`);
      } else {
        console.warn('[Variant Switcher] Could not get selected option');
      }
    } 
    // Otherwise use simple rules
    else if (VARIANT_RULES[productId]) {
      console.log('[Variant Switcher] Using simple rules');
      newVariantId = getVariantIdForQuantity(productId, quantity);
    } 
    else {
      console.warn('[Variant Switcher] No rules for this product');
      return; // No rules for this product
    }

    if (!newVariantId) {
      console.warn('[Variant Switcher] No variant ID calculated');
      return;
    }

    const variantInput = findVariantInput(form);
    if (!variantInput) {
      console.warn('[Variant Switcher] Could not find variant input');
      return;
    }

    const currentVariantId = variantInput.value;
    console.log('[Variant Switcher] Current variant:', currentVariantId, 'New variant:', newVariantId);

    // Only update if variant actually changed
    if (currentVariantId !== newVariantId.toString()) {
      console.log(`[Variant Switcher] ✓ Switching from ${currentVariantId} to ${newVariantId} for quantity ${quantity}`);
      updateVariantInput(variantInput, newVariantId);
      updatePriceDisplay(form, newVariantId, productId, selectedOption, quantity);
    } else {
      console.log('[Variant Switcher] Variant already correct, no change needed');
      // Still update price display in case quantity changed tiers
      updatePriceDisplay(form, newVariantId, productId, selectedOption, quantity);
    }
  }

  /**
   * Initialize variant switcher for a form
   */
  function initializeForm(form) {
    const quantityInput = findQuantityInput(form);
    if (!quantityInput) {
      console.warn('[Variant Switcher] Could not find quantity input in form');
      return;
    }

    // Handle quantity input changes
    quantityInput.addEventListener('input', function() {
      handleQuantityChange(form, quantityInput);
    });

    quantityInput.addEventListener('change', function() {
      handleQuantityChange(form, quantityInput);
    });

    // Handle increment/decrement buttons
    const quantityButtons = form.querySelectorAll('.quantity__button, [name="plus"], [name="minus"]');
    quantityButtons.forEach(button => {
      button.addEventListener('click', function() {
        setTimeout(() => {
          handleQuantityChange(form, quantityInput);
        }, 50);
      });
    });

    // Handle size/option changes (for advanced mode)
    const optionInputs = form.querySelectorAll('input[name^="options["], select[name^="options["]');
    optionInputs.forEach(input => {
      input.addEventListener('change', function() {
        setTimeout(() => {
          // When variant changes, enforce minimum quantity
          enforceMinimumQuantity(quantityInput);
          handleQuantityChange(form, quantityInput);
        }, 50);
      });
    });

    // Initial check on page load
    handleQuantityChange(form, quantityInput);

    console.log('[Variant Switcher] Initialized for form:', form);
  }

  /**
   * Initialize all product forms on the page
   */
  function initializeAllForms() {
    // Find all product forms
    const formSelectors = [
      'form[action*="/cart/add"]',
      'form[action="/cart/add"]',
      '[is="product-form"]',
      '.product-form',
      'product-form form'
    ];

    const forms = [];
    formSelectors.forEach(selector => {
      document.querySelectorAll(selector).forEach(form => {
        if (!forms.includes(form)) {
          forms.push(form);
        }
      });
    });

    if (forms.length === 0) {
      console.warn('[Variant Switcher] No product forms found');
      return;
    }

    forms.forEach(form => initializeForm(form));
  }

  // ============================================
  // INITIALIZATION
  // ============================================

  /**
   * Initialize when DOM is ready
   */
  function init() {
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', initializeAllForms);
    } else {
      initializeAllForms();
    }

    // Re-initialize when new content is loaded (for quick view, etc.)
    document.addEventListener('shopify:section:load', initializeAllForms);
    document.addEventListener('quickview:loaded', initializeAllForms);
  }

  // Start the script
  init();

  // Expose for debugging
  window.VariantSwitcher = {
    init: initializeAllForms,
    getVariantIdForQuantity: getVariantIdForQuantity
  };

})();
